import { motion } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HOLIDAY_PACKAGES } from "../../data/holidays";
import { Chapter, Reveal, useHolidayScroll, EASE } from "./shared";
import ShareTrip from "./ShareTrip";
import { useEarlyBird } from "../../lib/useEarlyBird";

export default function HolidayPackages() {
  const { t } = useTranslation();
  return (
    <section id="packages" className="bg-forest-deep text-bone py-24 lg:py-40 relative overflow-hidden scroll-mt-20" data-testid="holidays-packages">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-14 lg:mb-20">
          <div className="md:col-span-8">
            <Chapter number={t("holidays.packages.chapter")} label={t("holidays.packages.eyebrow")} tone="dark" />
            <Reveal delay={0.1}>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-8">
                {t("holidays.packages.title_a")} <span className="italic">{t("holidays.packages.title_b")}</span>.
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 items-stretch">
          {HOLIDAY_PACKAGES.map((p, i) => <PackageCard key={p.key} p={p} index={i} />)}
        </div>

        <Reveal delay={0.2} className="mt-12 lg:mt-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 sm:gap-8 border-t border-bone/15 pt-8">
          <p className="text-sm text-bone/80" data-testid="packages-note">
            <span className="text-bone">{t("holidays.packages.note_a")}</span>{" "}
            <span className="text-bone/70">{t("holidays.packages.note_b")}</span>
          </p>
          <ShareTrip className="shrink-0" />
        </Reveal>
      </div>
    </section>
  );
}

function PackageCard({ p, index }) {
  const { t } = useTranslation();
  const scrollTo = useHolidayScroll();
  const eb = useEarlyBird();
  const k = `holidays.packages.items.${p.key}`;
  const featured = p.featured;
  return (
    <motion.article
      data-testid={`package-card-${p.key}`}
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -60px 0px" }}
      transition={{ duration: 0.9, ease: EASE, delay: index * 0.12 }}
      whileHover={{ y: -6 }}
      className={`relative flex flex-col p-7 lg:p-9 border transition-colors duration-500 ${
        featured ? "bg-bone text-forest border-bone md:-my-6 md:py-14" : "bg-forest text-bone border-bone/15 hover:border-bone/40"
      }`}
    >
      {featured && (
        <div className="absolute top-0 left-7 lg:left-9 -translate-y-1/2 inline-flex items-center gap-2 bg-terracotta text-white text-[10px] uppercase tracking-wider-3 px-3 py-1.5" data-testid="package-featured-badge">
          <Star className="w-3 h-3 fill-current" /> {t("holidays.packages.featured_badge")}
        </div>
      )}
      <div className="flex items-start justify-between">
        <span className={`font-display text-2xl leading-none ${featured ? "text-moss" : "text-bone/50"}`}>0{index + 1}</span>
        <span className={`text-[10px] uppercase tracking-wider-3 ${featured ? "text-moss" : "text-bone/50"}`}>{p.nights} {p.nights === 1 ? "night" : "nights"}</span>
      </div>

      <h3 className="font-display text-3xl lg:text-4xl leading-tight mt-8 uppercase">{t(`${k}.duration`)}</h3>

      {featured && (
        <div className="mt-4 inline-flex self-start items-center gap-2 border border-forest px-3 py-1.5 text-[10px] uppercase tracking-wider-3" data-testid="package-riding-days">
          {t("holidays.packages.riding_days")}
        </div>
      )}

      <div className={`mt-8 pt-6 border-t ${featured ? "border-bone-deep" : "border-bone/15"}`}>
        <div className={`text-[10px] uppercase tracking-wider-3 ${featured ? "text-moss" : "text-bone/50"}`}>{t("holidays.packages.from")}</div>
        <div className="font-display text-6xl lg:text-7xl leading-none mt-1" data-testid={`package-price-${p.key}`}>{t(`${k}.price`)}</div>
        <div className={`text-xs mt-3 ${featured ? "text-muted-foreground" : "text-bone/60"}`}>{t("holidays.common.per_person")}</div>
        {eb.active && <button type="button" onClick={() => scrollTo("early-bird")} data-testid={`package-eb-hint-${p.key}`}
          className={`mt-3 text-left text-[10px] uppercase tracking-wider-2 link-underline ${featured ? "text-terracotta" : "text-terracotta/90"}`}>
          {t("holidays.packages.early_bird_hint")}
        </button>}
      </div>

      <p className={`mt-6 text-sm leading-relaxed flex-1 ${featured ? "text-muted-foreground" : "text-bone/75"}`}>{t(`${k}.text`)}</p>

      <button
        type="button"
        data-testid={`package-cta-${p.key}`}
        onClick={() => scrollTo("enquiry", p.key)}
        className={`btn-lift mt-8 inline-flex items-center justify-center gap-2 px-6 py-4 min-h-[48px] text-xs uppercase tracking-wider-3 ${
          featured ? "bg-forest text-bone hover:bg-forest-deep" : "border border-bone/40 text-bone hover:bg-bone hover:text-forest"
        }`}
      >
        {t("holidays.packages.cta")} <ArrowUpRight className="w-4 h-4" />
      </button>
    </motion.article>
  );
}
